import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, DollarSign, Target, MousePointer, Eye, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdsTracking = ({ userId }) => {
  const [adsData, setAdsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchAdsData();
    }
  }, [userId]);

  const fetchAdsData = async () => {
    try {
      const response = await axios.get(`${API}/ads/${userId}`);
      setAdsData(response.data);
    } catch (error) {
      console.error('Error fetching ads data:', error);
      setAdsData(null);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'];

  // Empty state or actual data
  const totalSpend = adsData?.totalSpend || 0;
  const impressions = adsData?.impressions || 0;
  const clicks = adsData?.clicks || 0;
  const conversions = adsData?.conversions || 0;
  const cpp = adsData?.cpp || 0;
  const campaignDuration = adsData?.campaignDuration || 0;
  const campaignStatus = adsData?.campaignStatus || 'Not Started';

  const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : 0;
  const conversionRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : 0;
  const cpc = clicks > 0 ? (totalSpend / clicks).toFixed(2) : 0;

  // Pie chart data for Ad Performance
  const performanceData = [
    { name: 'Impressions', value: impressions || 1 },
    { name: 'Clicks', value: clicks || 1 },
    { name: 'Conversions', value: conversions || 1 }
  ];

  // Pie chart for Budget Distribution
  const budgetData = [
    { name: 'Spent', value: totalSpend || 1 },
    { name: 'Remaining', value: Math.max(0, (adsData?.budget || 0) - totalSpend) || 1 }
  ];

  // Line chart for daily performance
  const dailyPerformance = adsData?.dailyStats || Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    spend: 0,
    conversions: 0
  }));

  return (
    <div className="space-y-6">
      {/* Campaign Status & Main Metrics */}
      <div className="grid [&>*]:min-w-0 grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-blue-400" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              ₹{totalSpend}
            </div>
            <div className="text-xs text-slate-600 mt-1">Total Spend</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {campaignDuration}
            </div>
            <div className="text-xs text-slate-600 mt-1">Campaign Days</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-8 h-8 text-orange-400" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">
              ₹{cpp}
            </div>
            <div className="text-xs text-slate-600 mt-1">CPP</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-lg font-bold text-blue-600">
              {campaignStatus}
            </div>
            <div className="text-xs text-slate-600 mt-1">Status</div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid [&>*]:min-w-0 grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-white shadow-lg border-slate-200">
          <CardContent className="pt-6">
            <Eye className="w-6 h-6 text-blue-400 mb-2" />
            <div className="text-2xl font-bold text-slate-800">{impressions}</div>
            <div className="text-xs text-slate-500">Impressions</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardContent className="pt-6">
            <MousePointer className="w-6 h-6 text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-slate-800">{clicks}</div>
            <div className="text-xs text-slate-500">Clicks</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardContent className="pt-6">
            <Target className="w-6 h-6 text-green-600 mb-2" />
            <div className="text-2xl font-bold text-slate-800">{conversions}</div>
            <div className="text-xs text-slate-500">Conversions</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardContent className="pt-6">
            <TrendingUp className="w-6 h-6 text-orange-400 mb-2" />
            <div className="text-2xl font-bold text-slate-800">{ctr}%</div>
            <div className="text-xs text-slate-500">CTR</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardContent className="pt-6">
            <Zap className="w-6 h-6 text-blue-500 mb-2" />
            <div className="text-2xl font-bold text-slate-800">{conversionRate}%</div>
            <div className="text-xs text-slate-500">Conv. Rate</div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Charts */}
      <div className="grid [&>*]:min-w-0 md:grid-cols-2 gap-4 md:gap-6">
        <Card className="bg-white shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="text-blue-600">Ad Performance Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {totalSpend === 0 && impressions === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center">
                  <Target className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-500">No campaign data yet</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer debounce={200} width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={performanceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {performanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F3F4F6' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="text-blue-600">Budget Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            {totalSpend === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-500">No budget data yet</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer debounce={200} width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={budgetData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {budgetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', color: '#F3F4F6' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Performance Chart */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Daily Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {totalSpend === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">No daily performance data yet</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer debounce={200} width="100%" height={300}>
              <LineChart data={dailyPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                <Legend />
                <Line type="monotone" dataKey="spend" stroke="#3B82F6" strokeWidth={2} />
                <Line type="monotone" dataKey="conversions" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdsTracking;